import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-black text-white py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-white hover:text-amber-500 transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms and Conditions</h1>
          <p className="text-gray-400 text-lg">Last Updated: March 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">1. Introduction and Acceptance</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                Welcome to LookWell Gents Salon. These Terms and Conditions ("Terms") govern your use of our salon services and website. By accessing our services, making an appointment, or visiting our salon located in Dubai, United Arab Emirates, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 text-base leading-relaxed">
                These Terms are governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">2. Services Provided</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                LookWell Gents Salon provides professional men's grooming services including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>Hair cutting, styling, and treatments</li>
                <li>Beard grooming and shaving services</li>
                <li>Facial treatments and skincare</li>
                <li>Massage and relaxation services</li>
                <li>Other grooming services as advertised</li>
              </ul>
              <p className="text-gray-700 text-base leading-relaxed mt-4">
                We reserve the right to modify, suspend, or discontinue any service at any time without prior notice.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">3. Appointments and Bookings</h2>
              <h3 className="text-xl font-bold text-black mb-3">3.1 Booking Process</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                Appointments can be made through our website, phone, or in person. All appointments are subject to availability and confirmation by our staff.
              </p>
              
              <h3 className="text-xl font-bold text-black mb-3">3.2 Cancellation Policy</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base mb-4">
                <li>Cancellations must be made at least 4 hours before the scheduled appointment time</li>
                <li>Late cancellations (less than 4 hours notice) may result in a cancellation fee</li>
                <li>No-shows may be charged the full service fee</li>
                <li>We reserve the right to refuse future bookings for repeated cancellations or no-shows</li>
              </ul>

              <h3 className="text-xl font-bold text-black mb-3">3.3 Late Arrivals</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                If you arrive more than 15 minutes late for your appointment, we may need to reschedule your service or reduce the service time to accommodate other clients. Full charges will still apply.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">4. Pricing and Payment</h2>
              <h3 className="text-xl font-bold text-black mb-3">4.1 Service Prices</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                All prices are listed in UAE Dirhams (AED) and are inclusive of applicable taxes as per UAE law. Prices are subject to change without notice, but confirmed appointments will be honored at the originally quoted price.
              </p>
              
              <h3 className="text-xl font-bold text-black mb-3">4.2 Payment Methods</h3>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We accept the following payment methods:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base mb-4">
                <li>Cash (AED)</li>
                <li>Credit and debit cards</li>
                <li>Digital payment methods (as available)</li>
              </ul>
              <p className="text-gray-700 text-base leading-relaxed">
                Payment is due upon completion of services unless otherwise agreed in advance.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">5. Client Responsibilities</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                As a client, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>Provide accurate contact information for appointment bookings</li>
                <li>Inform us of any allergies, skin sensitivities, or medical conditions that may affect services</li>
                <li>Arrive on time for scheduled appointments</li>
                <li>Treat our staff and other clients with respect</li>
                <li>Follow hygiene and safety guidelines provided by our salon</li>
                <li>Not use our services if you have any contagious conditions</li>
                <li>Supervise any minors accompanying you at all times</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">6. Health and Safety</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                We maintain high standards of hygiene and safety in accordance with UAE health regulations. However:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>You must disclose any known allergies or skin sensitivities before service</li>
                <li>We recommend a patch test for clients with sensitive skin before chemical treatments</li>
                <li>We are not liable for allergic reactions if you fail to disclose relevant information</li>
                <li>All tools and equipment are sanitized according to UAE health standards</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-3">
                To the fullest extent permitted by UAE law:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our liability for any claim is limited to the cost of the service provided</li>
                <li>We are not responsible for personal belongings left in the salon</li>
                <li>We are not liable for results if you fail to follow aftercare instructions</li>
                <li>We reserve the right to refuse service at our discretion</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">8. Service Guarantee and Complaints</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                We strive for 100% customer satisfaction. If you are not satisfied with our service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>Please inform us immediately so we can address the issue</li>
                <li>Complaints must be made within 48 hours of service</li>
                <li>We will attempt to rectify any issues at no additional charge</li>
                <li>Refunds are provided at our discretion on a case-by-case basis</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">9. Intellectual Property</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                All content on our website, including text, graphics, logos, images, and software, is the property of LookWell Gents Salon and protected by UAE and international intellectual property laws.
              </p>
              <p className="text-gray-700 text-base leading-relaxed">
                You may not reproduce, distribute, modify, or create derivative works from our content without express written permission.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">10. Photography and Marketing</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                We may take photographs of our work for marketing and promotional purposes. By using our services, you consent to such photography unless you explicitly opt out. We will not use images that clearly identify you without additional consent.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">11. Gift Vouchers and Promotions</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base">
                <li>Gift vouchers are valid for 12 months from date of purchase</li>
                <li>Vouchers are non-refundable and cannot be exchanged for cash</li>
                <li>Promotional offers cannot be combined unless stated otherwise</li>
                <li>We reserve the right to modify or cancel promotions at any time</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">12. Privacy and Data Protection</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                Your personal information is handled in accordance with our Privacy Policy and UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data. Please refer to our Privacy Policy for detailed information about data collection and usage.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">13. Force Majeure</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                We are not liable for failure to perform our obligations due to circumstances beyond our reasonable control, including but not limited to natural disasters, government actions, public health emergencies, or utility failures.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">14. Severability</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid under UAE law, such provision shall be modified to the minimum extent necessary, and the remaining provisions shall continue in full force and effect.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">15. Governing Law and Jurisdiction</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                These Terms and Conditions are governed by the laws of the United Arab Emirates. Any disputes arising from or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
              </p>
              <p className="text-gray-700 text-base leading-relaxed">
                Both parties agree to attempt to resolve any disputes through good faith negotiations before resorting to legal proceedings.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">16. Changes to Terms</h2>
              <p className="text-gray-700 text-base leading-relaxed">
                We reserve the right to modify these Terms at any time. Updated Terms will be posted on our website with a revised "Last Updated" date. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-black mb-4">17. Contact Information</h2>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                For questions, concerns, or inquiries about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 text-base mb-2"><strong>LookWell Gents Salon</strong></p>
                <p className="text-gray-700 text-base mb-2">Blue Building, Al Nahda 2</p>
                <p className="text-gray-700 text-base mb-2">Dubai, United Arab Emirates</p>
                <p className="text-gray-700 text-base mb-2">Phone: +971 564 740 785</p>
                <p className="text-gray-700 text-base">Email: inquiry@lookwellgentssalon.com</p>
              </div>
            </div>

            <div className="bg-amber-50 p-6 rounded-lg mt-12">
              <p className="text-gray-700 text-base leading-relaxed">
                <strong>Acknowledgment:</strong> By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;